import React, { useState } from 'react';
import { XCircle, Check } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

/**
 * ColorSwatchPicker
 * -----------------
 * Premium color control for the Carousel Designer. Three input paths kept
 * in sync via a single ``value`` prop:
 *
 *   1. Click any preset swatch  ->  fills the hex
 *   2. Click the native colour-well -> OS colour picker -> fills the hex
 *   3. Type / paste a hex directly  -> immediately applied
 *
 * The active swatch is highlighted with a check mark when ``value`` matches
 * one of the presets (case-insensitive).
 *
 * Props:
 *   value           : current hex string (e.g. "#0B0B0B"), or "" / null
 *   onChange(hex)   : called with the new hex string (always lowercase 7-char)
 *   onClear()       : optional - shows a clear button when set
 *   defaultValue    : hex string shown in the native picker when value is empty
 *   testIdPrefix    : prefix for data-testid on the inputs / swatches
 *
 * Palette is hand-tuned for executive / business decks:
 *   - 8 neutrals (true-black, slates, off-whites, true-white)
 *   - 8 cool primaries (navy / royal / sky)
 *   - 4 greens (deep emerald to mint)
 *   - 4 warm accents (amber, terracotta, burgundy)
 *
 * Total 24 cells laid out in 8 columns x 3 rows.
 */

export const CAROUSEL_SWATCHES = [
  // Row 1 - neutrals (light -> dark)
  { hex: '#FFFFFF', label: 'White' },
  { hex: '#F8FAFC', label: 'Off-white' },
  { hex: '#E5E7EB', label: 'Light grey' },
  { hex: '#9CA3AF', label: 'Mid grey' },
  { hex: '#475569', label: 'Slate' },
  { hex: '#1F2937', label: 'Charcoal' },
  { hex: '#0B1220', label: 'Ink' },
  { hex: '#000000', label: 'Black' },

  // Row 2 - cool primaries + accents
  { hex: '#0EA5E9', label: 'Sky' },
  { hex: '#2563EB', label: 'Royal' },
  { hex: '#1E3A8A', label: 'Navy' },
  { hex: '#0F172A', label: 'Midnight' },
  { hex: '#6D28D9', label: 'Purple' },
  { hex: '#9333EA', label: 'Violet' },
  { hex: '#14B8A6', label: 'Teal' },
  { hex: '#064E3B', label: 'Forest' },

  // Row 3 - greens + warm accents
  { hex: '#10B981', label: 'Emerald' },
  { hex: '#A7F3D0', label: 'Mint' },
  { hex: '#FACC15', label: 'Yellow' },
  { hex: '#F59E0B', label: 'Amber' },
  { hex: '#F97316', label: 'Orange' },
  { hex: '#DC2626', label: 'Red' },
  { hex: '#BE123C', label: 'Rose' },
  { hex: '#7C2D12', label: 'Terracotta' },
];

function normalizeHex(input) {
  if (!input) return '';
  let s = String(input).trim();
  if (!s) return '';
  if (!s.startsWith('#')) s = `#${s}`;
  // Expand #RGB to #RRGGBB
  if (/^#[0-9a-fA-F]{3}$/.test(s)) {
    s = `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
  }
  return s.toUpperCase();
}

function isValidHex(s) {
  return /^#[0-9a-fA-F]{6}$/.test(String(s || '').trim());
}

export default function ColorSwatchPicker({
  value,
  onChange,
  onClear,
  defaultValue = '#FFFFFF',
  testIdPrefix = 'color-picker',
  compact = false,
}) {
  const normalized = (value || '').toUpperCase();
  const [hexInput, setHexInput] = useState(normalized);

  // Keep the visible hex input in sync when ``value`` changes from the outside.
  React.useEffect(() => {
    setHexInput(normalized);
  }, [normalized]);

  const handleSwatchClick = (hex) => {
    const norm = normalizeHex(hex);
    onChange?.(norm);
    setHexInput(norm);
  };

  const handlePickerChange = (e) => {
    const norm = normalizeHex(e.target.value);
    onChange?.(norm);
    setHexInput(norm);
  };

  const handleHexBlur = () => {
    if (!hexInput) {
      onChange?.('');
      return;
    }
    if (isValidHex(hexInput)) {
      const norm = normalizeHex(hexInput);
      onChange?.(norm);
      setHexInput(norm);
    } else {
      // Invalid - revert to previous valid value
      setHexInput(normalized);
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-root`}>
      {/* Native picker + hex input + clear */}
      <div className="flex items-center gap-2">
        <div
          className="relative h-8 w-12 rounded border border-border overflow-hidden shrink-0"
          data-testid={`${testIdPrefix}-well`}
          style={{ background: normalized || defaultValue }}
        >
          <input
            type="color"
            value={normalized || defaultValue}
            onChange={handlePickerChange}
            className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
            aria-label="Open OS colour picker"
            data-testid={`${testIdPrefix}-native`}
          />
        </div>
        <Input
          value={hexInput}
          onChange={(e) => setHexInput(e.target.value)}
          onBlur={handleHexBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleHexBlur();
            }
          }}
          placeholder="#hex (blank = template default)"
          className="h-8 text-xs font-mono-edge"
          data-testid={`${testIdPrefix}-hex`}
        />
        {onClear && normalized && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange?.('');
              setHexInput('');
              onClear();
            }}
            data-testid={`${testIdPrefix}-clear`}
            aria-label="Clear colour"
          >
            <XCircle size={13} />
          </Button>
        )}
      </div>

      {/* Curated palette - click a swatch to autofill hex */}
      <div
        className={cn(
          'grid grid-cols-8 gap-1.5 rounded-md border border-border/80 bg-secondary/40 p-2',
          compact && 'gap-1 p-1.5',
        )}
        role="listbox"
        aria-label="Preset colours"
        data-testid={`${testIdPrefix}-palette`}
      >
        {CAROUSEL_SWATCHES.map((sw) => {
          const isActive = normalized === sw.hex.toUpperCase();
          // Pale colours need a dark check mark for contrast.
          const checkDark = ['#FFFFFF', '#F8FAFC', '#E5E7EB', '#A7F3D0', '#FACC15'].includes(sw.hex);
          return (
            <button
              key={sw.hex}
              type="button"
              role="option"
              aria-selected={isActive}
              aria-label={`${sw.label} (${sw.hex})`}
              title={`${sw.label}  ·  ${sw.hex}`}
              onClick={() => handleSwatchClick(sw.hex)}
              data-testid={`${testIdPrefix}-swatch-${sw.hex.replace('#', '').toLowerCase()}`}
              className={cn(
                'group relative aspect-square w-full rounded-md border border-border/70 transition-transform',
                'hover:scale-110 hover:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1',
                isActive && 'ring-2 ring-primary ring-offset-2 ring-offset-background scale-105',
              )}
              style={{ backgroundColor: sw.hex }}
            >
              {isActive && (
                <Check
                  size={12}
                  className={cn(
                    'absolute inset-0 m-auto drop-shadow-sm',
                    checkDark ? 'text-black' : 'text-white',
                  )}
                  strokeWidth={3}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
